import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import DictationInput from '../components/DictationInput';
import ToneSelector from '../components/ToneSelector';
import { ClipboardList, Plus, Mic, Sparkles } from 'lucide-react';
import { formatDate } from '../lib/utils';

export default function PeerDocsPage() {
  const { user, axiosAuth } = useAuth();
  const [docs, setDocs] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: '', spoken_text: '', interaction_type: 'one_on_one' });
  const [creating, setCreating] = useState(false);
  const api = axiosAuth();

  const load = () => {
    api.get('/peer-docs').then(r => setDocs(r.data)).catch(() => {});
    api.get('/users?role=client').then(r => setClients(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.client_id) return toast.error('Select a person supported');
    if (!form.spoken_text.trim()) return toast.error('Please add documentation content');
    setCreating(true);
    try {
      await api.post('/peer-docs', form);
      toast.success('Peer support documentation saved! AI has reframed it in proper peer support language.');
      setOpen(false);
      setForm({ client_id: '', spoken_text: '', interaction_type: 'one_on_one' });
      load();
    } catch (err) {
      toast.error('Failed to save documentation');
    } finally {
      setCreating(false);
    }
  };

  const typeLabels = { one_on_one: 'One-on-One', phone: 'Phone', group: 'Group', community: 'Community' };

  return (
    <div className="space-y-6" data-testid="peer-docs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Peer Support Documentation</h1>
          <p className="text-muted-foreground text-sm mt-1">Document interactions using peer support language — speak or type</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="new-peer-doc-button">
              <Plus size={16} /> New Entry
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl max-w-lg">
            <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Document Peer Support Interaction</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="bg-teal-50 rounded-xl p-3 border border-teal-100">
                <p className="text-xs text-teal-700 font-medium">
                  <Sparkles size={12} className="inline mr-1" />
                  AI will automatically reframe your notes into proper peer support language — person-centered, strengths-based, and recovery-oriented. No clinical terms.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm">Person Supported</Label>
                <Select value={form.client_id} onValueChange={v => setForm({...form, client_id: v})}>
                  <SelectTrigger data-testid="peer-doc-client-select"><SelectValue placeholder="Select person" /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm">Interaction Type</Label>
                <Select value={form.interaction_type} onValueChange={v => setForm({...form, interaction_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_on_one">One-on-One</SelectItem>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="group">Group</SelectItem>
                    <SelectItem value="community">Community</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">What happened? (Speak or type)</Label>
                <div className="bg-muted/30 rounded-xl p-3 text-xs text-muted-foreground space-y-1 mb-2">
                  <p className="font-semibold text-foreground text-xs mb-1">Prompts to guide your documentation:</p>
                  <p>1. Where did the meeting occur?</p>
                  <p>2. What topics were discussed?</p>
                  <p>3. What services were provided?</p>
                  <p>4. How did the participant appear emotionally?</p>
                  <p>5. What was the outcome of the meeting?</p>
                </div>
                <DictationInput
                  value={form.spoken_text}
                  onChange={(cleaned) => setForm(f => ({...f, spoken_text: cleaned}))}
                  rows={5}
                  placeholder="Describe the interaction — address the prompts above: where you met, topics discussed, services provided, emotional presentation, and outcomes..."
                  promptContext={{ context: 'session_note' }}
                  data-testid="peer-doc-text-input"
                />
              </div>

              {form.spoken_text.trim() && (
                <ToneSelector text={form.spoken_text} context="peer_support" onSelect={(text) => setForm({...form, spoken_text: text})} />
              )}

              <Button onClick={handleCreate} disabled={creating} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="save-peer-doc-button">
                {creating ? 'Saving & Reframing...' : 'Save Documentation'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {docs.length > 0 ? docs.map(d => (
          <Card key={d.id} className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-teal-50 text-teal-700" variant="secondary">{typeLabels[d.interaction_type] || d.interaction_type}</Badge>
                    <Badge className="bg-emerald-50 text-emerald-700" variant="secondary">{d.status}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">By {d.specialist_name} &middot; {formatDate(d.created_at)}</p>
                </div>
              </div>
              <div className="bg-teal-50/50 rounded-xl p-3 border border-teal-100">
                <p className="text-xs font-semibold text-teal-600 uppercase mb-1">Peer Support Documentation</p>
                <p className="text-sm leading-relaxed">{d.reframed_text}</p>
              </div>
              {d.original_text !== d.reframed_text && (
                <details className="mt-2">
                  <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">View original notes</summary>
                  <p className="text-xs text-muted-foreground mt-1 bg-muted/30 rounded-lg p-2">{d.original_text}</p>
                </details>
              )}
            </CardContent>
          </Card>
        )) : (
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-8 text-center">
              <ClipboardList className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No peer support documentation yet.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
